import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CoOwnershipModal from "@/components/feature/CoOwnershipModal";
import { sampleProperties, getPropertyDetail, parsePrice } from "@/mocks/properties";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

function formatUSD(jpyStr: string): string {
  const n = parsePrice(jpyStr);
  if (!n) return "—";
  return `$${Math.round(n / 150).toLocaleString()}`;
}

export default function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const property = sampleProperties.find((p) => p.id === id);
  const detail = getPropertyDetail(id || "");
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [coModalOpen, setCoModalOpen] = useState(false);
  const { t, i18n } = useTranslation();

  if (!property || !detail) {
    return (
      <div className="min-h-screen bg-paper">
        <Navbar />
        <main className="pt-24 md:pt-32 pb-20 px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center py-20">
            <p className="text-ink/40 font-light mb-4">{t("props.propertyNotFound")}</p>
            <Link
              to="/properties"
              className="inline-flex items-center gap-2 text-sm font-medium text-gold hover:text-ink transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {t("btn.backToProperties")}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const gallery = detail.gallery;
  const currentImg = gallery[galleryIndex];

  const next = () => setGalleryIndex((i) => (i + 1) % gallery.length);
  const prev = () => setGalleryIndex((i) => (i - 1 + gallery.length) % gallery.length);

  const cgCount = gallery.filter((g) => g.type === "cg").length;
  const currentCount = gallery.filter((g) => g.type === "current").length;

  return (
    <div className="min-h-screen bg-paper">
      <Navbar />
      <main className="pt-24 md:pt-32 pb-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-ink/40 mb-6 flex-wrap">
            <Link to="/properties" className="hover:text-ink transition-colors cursor-pointer">
              {t("detail.breadcrumbHomes")}
            </Link>
            <i className="ri-arrow-right-s-line text-ink/20"></i>
            <span>{property.region}</span>
            <i className="ri-arrow-right-s-line text-ink/20"></i>
            <span>{property.typeLabel}</span>
          </nav>

          {/* Title block */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="bg-vermilion text-paper text-xs font-semibold px-2.5 py-1">
                  {t("label.offMarket")}
                </span>
                <span className="text-xs text-ink/40 uppercase tracking-wider">
                  {property.id}
                </span>
              </div>
              <h1 className="font-serif text-3xl md:text-4xl font-medium text-ink leading-tight mb-2">
                {property.headline}
              </h1>
              <p className="text-sm text-ink/50">{property.region}</p>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-serif text-3xl font-semibold text-ink">
                {formatUSD(property.priceJPY)}
              </span>
              <span className="text-sm text-ink/40">
                ({property.priceJPY})
              </span>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-10">
            {/* Main column */}
            <div className="flex-1 min-w-0">
              {/* Gallery */}
              <div className="mb-10">
                <div className="relative aspect-[16/10] bg-ink-light overflow-hidden mb-3 flex items-center justify-center">
                  <img
                    src={currentImg.src}
                    alt={currentImg.alt}
                    loading="lazy"
                    className="w-full h-full object-contain"
                    style={{ objectPosition: "center" }}
                  />
                  {currentImg.type === "cg" && (
                    <span className="absolute top-3 left-3 bg-vermilion text-paper text-xs font-semibold px-3 py-1.5">
                      {t("label.cg")}
                    </span>
                  )}
                  {currentImg.type === "current" && (
                    <span className="absolute top-3 left-3 bg-ink text-paper text-xs font-semibold px-3 py-1.5">
                      {t("label.current")}
                    </span>
                  )}
                  {(currentImg as { type?: string }).type === "factsheet" && (
                    <span className="absolute top-3 left-3 bg-gold text-ink text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5">
                      <i className="ri-file-text-line text-xs"></i>
                      English fact sheet
                    </span>
                  )}
                  {(currentImg as { type?: string }).type === "factsheet" && (
                    <a
                      href={currentImg.src}
                      download={`${property.id}-fact-sheet.jpg`}
                      className="absolute top-3 right-3 bg-ink/80 hover:bg-vermilion text-paper text-xs font-medium px-3 py-1.5 flex items-center gap-1.5 transition-colors cursor-pointer"
                      title="Download English fact sheet"
                    >
                      <i className="ri-download-line"></i>
                      Download
                    </a>
                  )}
                  <button
                    onClick={prev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-ink/60 hover:bg-ink text-paper flex items-center justify-center transition-colors cursor-pointer"
                    aria-label="Previous image"
                  >
                    <i className="ri-arrow-left-line"></i>
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-ink/60 hover:bg-ink text-paper flex items-center justify-center transition-colors cursor-pointer"
                    aria-label="Next image"
                  >
                    <i className="ri-arrow-right-line"></i>
                  </button>
                  <span className="absolute bottom-3 right-3 bg-ink/70 text-paper text-xs px-2.5 py-1">
                    {t("label.of", { current: galleryIndex + 1, total: gallery.length })}
                  </span>
                </div>

                {/* Thumbnails */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {gallery.map((g, i) => (
                    <button
                      key={i}
                      onClick={() => setGalleryIndex(i)}
                      className={`relative flex-shrink-0 w-20 h-14 overflow-hidden border-2 transition-colors cursor-pointer ${
                        i === galleryIndex ? "border-vermilion" : "border-transparent"
                      }`}
                    >
                      <img
                        src={g.src}
                        alt={g.alt}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                      <span
                        className={`absolute top-0.5 left-0.5 text-[9px] font-semibold px-1 py-0.5 ${
                          (g as { type?: string }).type === "cg"
                            ? "bg-vermilion text-paper"
                            : (g as { type?: string }).type === "factsheet"
                            ? "bg-gold text-ink"
                            : "bg-ink text-paper"
                        }`}
                      >
                        {(g as { type?: string }).type === "cg"
                          ? t("label.cgShort")
                          : (g as { type?: string }).type === "factsheet"
                          ? "EN"
                          : t("label.currentShort")}
                      </span>
                    </button>
                  ))}
                </div>

                {cgCount > 0 && currentCount > 0 && (
                  <p className="text-xs text-ink/40 mt-2 leading-relaxed">
                    {t("detail.cgCaption", {
                      cgCount,
                      cgCountPlural: cgCount > 1 ? (i18n.language === "ja" ? "枚" : "s") : (i18n.language === "ja" ? "枚" : ""),
                      cgCountVerb: i18n.language === "ja" ? "は" : "are",
                      currentCount,
                    })}
                  </p>
                )}
              </div>

              {/* The opportunity */}
              <section className="mb-10">
                <h2 className="font-serif text-xl text-ink mb-3">{t("detail.opportunity")}</h2>
                <p className="text-sm text-ink/60 leading-relaxed">{detail.opportunity}</p>
              </section>

              {/* Specifications */}
              <section className="mb-10">
                <h2 className="font-serif text-xl text-ink mb-4">{t("detail.specifications")}</h2>
                <div className="border border-hairline bg-white">
                  {Object.entries(detail.specs).map(([label, value], idx) => (
                    <div
                      key={label}
                      className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-0 px-4 py-3 ${
                        idx !== 0 ? "border-t border-hairline" : ""
                      }`}
                    >
                      <span className="sm:w-48 text-xs font-medium text-ink/40 uppercase tracking-wider flex-shrink-0">
                        {label}
                      </span>
                      {value ? (
                        <span className="text-sm text-ink/80">{value}</span>
                      ) : (
                        <span className="text-sm text-vermilion italic">
                          {t("detail.unknownSpec", {
                            field: label === "Floor plan" ? (i18n.language === "ja" ? "間取り" : "floor plan") : label.toLowerCase(),
                          })}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Renovation plan */}
              <section className="mb-10">
                <h2 className="font-serif text-xl text-ink mb-4">{t("detail.renovationPlan")}</h2>
                <ul className="space-y-3">
                  {detail.renovationPlan.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-paper-secondary border border-hairline flex items-center justify-center text-xs font-medium text-ink/50 mt-0.5">
                        {i + 1}
                      </span>
                      <span className="text-sm text-ink/60 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Indicative all-in & return */}
              <section className="mb-10">
                <h2 className="font-serif text-xl text-ink mb-4">{t("detail.indicativeReturn")}</h2>
                <div className="border border-hairline bg-white">
                  {detail.indicativeReturn.map((row, idx) => (
                    <div
                      key={row.label}
                      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-0 px-4 py-3 ${
                        idx !== 0 ? "border-t border-hairline" : ""
                      }`}
                    >
                      <span className="text-sm text-ink/60">{row.label}</span>
                      <div className="text-right">
                        <span className="text-sm font-medium text-ink">{row.value}</span>
                        {row.note && (
                          <span className="block text-[10px] text-ink/30">{row.note}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-ink/30 mt-2 leading-relaxed">
                  {t("detail.disclaimer")}
                </p>
              </section>

              {/* Area */}
              <section className="mb-10">
                <h2 className="font-serif text-xl text-ink mb-3">{t("detail.area")}</h2>
                <p className="text-sm text-ink/60 leading-relaxed mb-4">{detail.areaNote}</p>
                <div className="aspect-[16/9] bg-paper-secondary border border-hairline flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-map-pin-line text-2xl text-ink/20 mb-2 block"></i>
                    <p className="text-sm text-ink/40">{t("detail.mapPlaceholder")}</p>
                  </div>
                </div>
              </section>

              {/* Buy it together */}
              <section className="mb-10">
                <div className="border border-hairline bg-white p-6">
                  <h3 className="font-serif text-lg text-ink mb-2">{t("detail.buyTogether")}</h3>
                  <p className="text-sm text-ink/50 leading-relaxed mb-4">
                    {t("detail.buyTogetherDesc")}
                  </p>
                  <button
                    onClick={() => setCoModalOpen(true)}
                    className="inline-flex items-center gap-2 bg-vermilion text-paper text-sm font-medium px-5 py-2.5 hover:bg-vermilion/90 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-group-line"></i>
                    {t("btn.openCoListing")}
                  </button>
                </div>
              </section>
            </div>

            {/* Sticky sidebar */}
            <aside className="w-full lg:w-80 flex-shrink-0">
              <div className="lg:sticky lg:top-24 space-y-4">
                <div className="border border-hairline bg-white p-5">
                  <p className="text-xs text-ink/30 uppercase tracking-wider mb-1">{t("label.price")}</p>
                  <p className="font-serif text-2xl font-semibold text-ink mb-1">
                    {formatUSD(property.priceJPY)}
                  </p>
                  <p className="text-sm text-ink/40 mb-5">
                    {property.priceJPY} <span className="text-ink/30">(¥150 ≈ $1)</span>
                  </p>

                  <div className="space-y-2">
                    <a
                      href="#inquiry"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="block w-full text-center bg-ink text-paper text-sm font-medium px-4 py-2.5 hover:bg-indigo transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t("btn.requestFullDetails")}
                    </a>
                    <a
                      href="#inquiry"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="block w-full text-center border border-hairline text-ink text-sm font-medium px-4 py-2.5 hover:bg-paper-secondary transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t("btn.bookCall")}
                    </a>
                    <button
                      onClick={() => setCoModalOpen(true)}
                      className="block w-full text-center bg-vermilion text-paper text-sm font-medium px-4 py-2.5 hover:bg-vermilion/90 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t("btn.openCoListing")}
                    </button>
                    <a
                      href={buildWhatsAppUrl(property.id, property.headline, i18n.language)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center bg-green-600 text-paper text-sm font-medium px-4 py-2.5 hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line mr-1"></i>
                      {t("btn.whatsapp")}
                    </a>
                  </div>
                </div>

                <div className="border-l-4 border-vermilion bg-[#fbeee6] p-4">
                  <p className="text-xs text-ink/60 leading-relaxed">
                    <strong className="text-ink/80">{t("detail.offMarketNotice")}</strong> {t("detail.offMarketNoticeText")}
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />

      <CoOwnershipModal
        isOpen={coModalOpen}
        onClose={() => setCoModalOpen(false)}
        mode="open"
        defaultAllIn={detail.defaultAllIn}
      />
    </div>
  );
}